/* eslint-disable @typescript-eslint/no-unused-vars */
import { AlertToastType, ToastController } from '@boomi/exosphere';
import Auth from '@pages/BoomiAI/auth'; // BoomiAI stays in pages
import Util from '@pages/BoomiAI/util';
import { transformParamsFromApi } from '@components/ToolManagement/helpers/oauthHelpers';
import {
  buildJevModelFromReference,
  isJevModel,
} from '@components/ModelsManagement/jevModel';
import { AGENT_MODE_DECISION, isSchemaMode, toBackendAgentMode } from '@components/AgentBuilderWizard/agentMode';

const agentFoundationApiService = {
};

interface ApiResponse<T = any> {
  data: T; // The actual data from the response
  status: number; // HTTP status code
}

export interface SourceNameFilterOption {
  id: string;
  created_on: string;
  last_updated_on: string;
  created_by: string;
  last_updated_by: string;
  last_used_on: string | null;
  installed_on: string | null;
  pre_installed: boolean;
  name: string;
  description: string;
  url: string;
  type: string;
  status: string;
  provider_name: string;
  agents_equipped: number;
  imported_tools_count: number;
}

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterOptionsResponse {
  success: boolean;
  message: string;
  data: {
    tool_types: FilterOption[];
    tool_statuses: FilterOption[];
    source_types: FilterOption[];
    application_names: FilterOption[];
  };
}
function getAPIcall(apiUrl: string, endpointUrl?: string) {
  let endpointUrlHost = endpointUrl;
  if (endpointUrl) {
    endpointUrlHost = Util.getEndpointUrlHost(endpointUrl);
  }
  const host = endpointUrlHost || Util.getAWSHostForAgentFoundation();
  const jwt = Util.getCookie('JWT');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${jwt}`,
  };

  return new Promise((resolve, reject) => {
    fetch(`${host}${apiUrl}`, {
      method: 'GET',
      headers,
    })
      .then((response) => response.json().then((data: any) => {
        if (data.success) {
          const resData = {
            data: data?.data,
            status: response.status,
            success: data.success,
            next_cursor: data?.next_cursor,
            previous_cursor: data?.previous_cursor,
          };
          resolve(resData);
        } else {
          const resData = {
            message: data?.error,
            status: response.status,
            success: data.success,
          };
          resolve(resData);
        }
      }))
      .catch((error) => {
        // Handle network or fetch errors
        reject(new Error(`Network error or invalid API endpoint: ${error.message}`));
      });
  });
}
function CanUseExistingJwtToken(apiUrl: string) {
  const endpointsRequiringNewToken = [
    'environment-atom-attachments',
    'deployed-processes',
    'repositories',
    'universe',
    'environments',
  ];
  return !endpointsRequiringNewToken.some((substr) => apiUrl.includes(substr));
}
export function getApi(apiUrl: string, endpointUrl?: string) {
  if (CanUseExistingJwtToken(apiUrl) && Auth.isJwtValid()) {
    return getAPIcall(apiUrl, endpointUrl);
  }

  return Auth.obtainJWTToken()
    .then((jwt) => {
      if (jwt) {
        Util.setCookie('JWT', jwt);
        return getAPIcall(apiUrl);
      }
      return Promise.resolve();
    })
    .catch((error) => error);
}
function getApiWithAccountIdHeaderCall(apiUrl: string, query_token?: string) {
  const host = Util.getAWSHostForAgentFoundation();
  const accountId = Util.getCookie('ACCOUNT_ID');
  const jwt = Util.getCookie('JWT');
  const headers = {
    'Content-Type': 'application/json',
    'x-account-id': accountId || '',
    // query token to get more processes
    ...(query_token && { query_token }),
    // Add Authorization if required:
    Authorization: `Bearer ${jwt}`,
  };

  return new Promise((resolve, reject) => {
    fetch(`${host}${apiUrl}`, {
      method: 'GET',
      headers,
    })
      .then((response) => response.json().then((data: any) => {
        if (data.success) {
          const resData = { data: data?.data, status: response.status, success: data.success };
          resolve(resData);
        } else {
          const resData = {
            message: data?.error,
            status: response.status,
            success: data.success,
          };
          resolve(resData);
        }
      }))
      .catch((error) => {
        // Handle network or fetch errors
        reject(new Error(`Network error or invalid API endpoint: ${error.message}`));
      });
  });
}

export function getApiWithAccountIdHeader(apiUrl: string, query_token?: string) {
  if (CanUseExistingJwtToken(apiUrl) && Auth.isJwtValid()) {
    return query_token
      ? getApiWithAccountIdHeaderCall(apiUrl, query_token)
      : getApiWithAccountIdHeaderCall(apiUrl);
  }

  return Auth.obtainJWTToken()
    .then((jwt) => {
      if (jwt) {
        Util.setCookie('JWT', jwt);
        return query_token
          ? getApiWithAccountIdHeaderCall(apiUrl, query_token)
          : getApiWithAccountIdHeaderCall(apiUrl);
      }
      return Promise.resolve();
    })
    .catch((error) => error);
}

function postApiWithAccountIdHeaderCall(apiUrl: string, payload: any) {
  const host = Util.getAWSHostForAgentFoundation();
  const accountId = Util.getCookie('ACCOUNT_ID');
  const jwt = Util.getCookie('JWT');
  const headers = {
    'Content-Type': 'application/json',
    'x-account-id': accountId || '',
    // Add Authorization if required:
    Authorization: `Bearer ${jwt}`,
  };

  return new Promise((resolve, reject) => {
    fetch(`${host}${apiUrl}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    })
      .then((response) => response.json().then((data: any) => {
        if (data.success) {
          const resData = { data: data?.data, status: response.status, success: data.success };
          resolve(resData);
        } else {
          const resData = {
            message: data?.error,
            status: response.status,
            success: data.success,
          };
          resolve(resData);
        }
      }))
      .catch((error) => {
        // Handle network or fetch errors
        reject(new Error(`Network error or invalid API endpoint: ${error.message}`));
      });
  });
}

export function postApiWithAccountIdHeader(apiUrl: string, payload: any) {
  if (CanUseExistingJwtToken(apiUrl) && Auth.isJwtValid()) {
    return postApiWithAccountIdHeaderCall(apiUrl, payload);
  }

  return Auth.obtainJWTToken()
    .then((jwt) => {
      if (jwt) {
        Util.setCookie('JWT', jwt);
        return postApiWithAccountIdHeaderCall(apiUrl, payload);
      }
      return Promise.resolve();
    })
    .catch((error) => error);
}

export function postApiCall(
  apiUrl: string,
  payload: any,
  signal?: AbortSignal,
  endpointUrl?: string,
) {
  const host = endpointUrl || Util.getAWSHostForAgentFoundation();
  const jwt = Util.getCookie('JWT');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${jwt}`,
  };

  return new Promise((resolve, reject) => {
    fetch(`${host}${apiUrl}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal, // Add abort signal support
    })
      .then((response) => {
        response.json().then((data: any) => {
          if (data.success) {
            const resData = {
              data: data?.data,
              status: response.status,
              success: data.success,
              message: data?.message,
            };
            resolve(resData);
          } else {
            const resData = {
              message: data?.error,
              status: response.status,
              success: data.success,
              authorization_url: data?.authorization_url,
              sign_in_message: data?.sign_in_message,
              request_id: data?.request_id,
            };
            resolve(resData);
          }
        });
      })
      .catch((error) => reject(error));
  });
}
export function postApi(apiUrl: string, payload: any, signal?: AbortSignal, endpointUrl?: string) {
  if (Auth.isJwtValid()) {
    return postApiCall(apiUrl, payload, signal, endpointUrl);
  }

  return Auth.obtainJWTToken()
    .then((jwt) => {
      if (jwt) {
        Util.setCookie('JWT', jwt);
        return postApiCall(apiUrl, payload, signal);
      }
      return Promise.resolve();
    })
    .catch((error) => error);
}

export function postAgentExportPreviewApiCall(apiUrl: string, payload: any) {
  const host = `${Util.getAWSHostForAgentFoundation()}`;
  const jwt = Util.getCookie('JWT');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${jwt}`,
  };
  return new Promise((resolve, reject) => {
    fetch(`${host}${apiUrl}`, {
      method: 'POST',
      headers,
    })
      .then((response: any) => {
        resolve(response);
      })
      .catch((error) => reject(error));
  });
}

// Full-envelope POST (returns entire response JSON, including trace, success, etc.)
export function postApiFullCall(apiUrl: string, payload: any, signal?: AbortSignal, host?: string) {
  let endpointUrlHost;
  if (host) {
    endpointUrlHost = Util.getEndpointUrlHost(host);
  }
  const finalHost = endpointUrlHost || `${Util.getAWSHostForAgentFoundation()}`;
  const jwt = Util.getCookie('JWT');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${jwt}`,
  };

  return new Promise((resolve, reject) => {
    fetch(`${finalHost}${apiUrl}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal,
    })
      .then((response) => {
        response.json().then((data: any) => {
          // keep full envelope and also attach status for convenience
          resolve({ ...data, status: response.status });
        }).catch((err) => {
          reject(err instanceof Error ? err : new Error(String(err)));
        });
      })
      .catch((error) => reject(error instanceof Error ? error : new Error(String(error))));
  });
}

export function postApiFull(apiUrl: string, payload: any, signal?: AbortSignal, host?: string) {
  if (Auth.isJwtValid()) {
    return postApiFullCall(apiUrl, payload, signal, host);
  }

  return Auth.obtainJWTToken()
    .then((jwt) => {
      if (jwt) {
        Util.setCookie('JWT', jwt);
        return postApiFullCall(apiUrl, payload, signal, host);
      }
      return Promise.resolve();
    })
    .catch((error) => error);
}

export function exportAgent(apiUrl: string, payload: any) {
  if (Auth.isJwtValid()) {
    return postAgentExportPreviewApiCall(apiUrl, payload);
  }

  return Auth.obtainJWTToken()
    .then((jwt) => {
      if (jwt) {
        Util.setCookie('JWT', jwt);
        return postAgentExportPreviewApiCall(apiUrl, payload);
      }
      return Promise.reject(new Error('Failed to obtain JWT token'));
    })
    .catch((error) => error);
}

export function postAgentExportDownloadApiCall(apiUrl: string) {
  const host = `${Util.getAWSHostForAgentFoundation()}`;
  const jwt = Util.getCookie('JWT');
  const headers = {
    'Content-Type': 'application/zip',
    Authorization: `Bearer ${jwt}`,
  };
  return new Promise((resolve, reject) => {
    fetch(`${host}${apiUrl}`, {
      method: 'POST',
      headers,
    })
      .then((response: any) => {
        resolve(response);
      })
      .catch((error) => reject(error));
  });
}

export function agentExportDownload(apiUrl: string) {
  if (Auth.isJwtValid()) {
    return postAgentExportDownloadApiCall(apiUrl);
  }

  return Auth.obtainJWTToken()
    .then((jwt) => {
      if (jwt) {
        Util.setCookie('JWT', jwt);
        postAgentExportDownloadApiCall(apiUrl);
      }
      return Promise.resolve();
    })
    .catch((error) => error);
}

export function postParsingApiCall<T = any>(apiUrl: string, payload: any): Promise<ApiResponse<T>> {
  const host = `${Util.getAWSHostForAgentFoundation()}`;
  const jwt = Util.getCookie('JWT');
  const headers = {
    Authorization: `Bearer ${jwt}`,
  };
  return new Promise((resolve, reject) => {
    fetch(`${host}${apiUrl}`, {
      method: 'POST',
      headers,
      body: payload,
    })
      .then((response) => {
        response.json().then((data: any) => {
          const resData = {
            data: data?.data,
            status: response.status,
            success: data.success,
            errors: data?.errors,
            error: data?.error,
          };
          resolve(resData);
        });
      })
      .catch((error) => reject(error));
  });
}
export function postParsingApi<T = any>(apiUrl: string, payload: any): Promise<ApiResponse<T>> {
  if (Auth.isJwtValid()) {
    return postParsingApiCall(apiUrl, payload);
  }

  return Auth.obtainJWTToken()
    .then((jwt) => {
      if (jwt) {
        Util.setCookie('JWT', jwt);
        return postParsingApiCall(apiUrl, payload);
      }
      return Promise.resolve({});
    })
    .catch((error) => error);
}
export function putApiCall(apiUrl: string, payload: any) {
  const host = `${Util.getAWSHostForAgentFoundation()}`;
  const jwt = Util.getCookie('JWT');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${jwt}`,
  };

  return new Promise((resolve, reject) => {
    fetch(`${host}${apiUrl}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(payload),
    })
      .then((response: any) => {
        response.json().then((data: any) => {
          if (data.success) {
            const resData = {
              data: data?.data,
              status: response.status,
              success: data.success,
              message: data.message,
            };
            resolve(resData);
          } else {
            const resData = {
              message: data?.error,
              status: response.status,
              success: data.success,
              authorization_url: data?.authorization_url,
              sign_in_message: data?.sign_in_message,
              request_id: data?.request_id,
            };
            resolve(resData);
          }
        });
      })
      .catch((error) => reject(error));
  });
}
export function putApi(apiUrl: string, payload: any) {
  if (Auth.isJwtValid()) {
    return putApiCall(apiUrl, payload);
  }

  return Auth.obtainJWTToken()
    .then((jwt) => {
      if (jwt) {
        Util.setCookie('JWT', jwt);
        return putApiCall(apiUrl, payload);
      }
      return Promise.resolve({});
    })
    .catch((error) => error);
}
export function deleteApiCall(apiUrl: string, endpointUrl?: string) {
  const host = endpointUrl || Util.getAWSHostForAgentFoundation();
  const jwt = Util.getCookie('JWT');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${jwt}`,
  };

  return new Promise((resolve, reject) => {
    fetch(`${host}${apiUrl}`, {
      method: 'DELETE',
      headers,
      // body: JSON.stringify(payload),
    })
      .then((response) => {
        response.json().then((data: any) => {
          if (data.success) {
            const resData = { data: data?.data, status: response.status, success: data.success };
            resolve(resData);
          } else {
            const resData = {
              message: data?.error,
              status: response.status,
              success: data.success,
            };
            resolve(resData);
          }
        });
      })
      .catch((error) => reject(error));
  });
}
export function deleteApi(apiUrl: string, endpointUrl?: string) {
  if (Auth.isJwtValid()) {
    return deleteApiCall(apiUrl, endpointUrl);
  }

  return Auth.obtainJWTToken()
    .then((jwt) => {
      if (jwt) {
        Util.setCookie('JWT', jwt);
        return deleteApiCall(apiUrl);
      }
      return Promise.resolve({});
    })
    .catch((error) => error);
}
export function sendTestMessageWithStreamCall(
  apiUrl: string,
  payload: any,
  signal: any,
  host?: string,
): Promise<any> {
  let endpointUrlHost = host;
  if (host) {
    endpointUrlHost = Util.getEndpointUrlHost(host);
  }
  const finalHost = endpointUrlHost || `${Util.getAWSHostForAgentFoundation()}`;
  const jwt = Util.getCookie('JWT');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${jwt}`,
    'X-Requested-By': 'AgentstudioUI',
  };

  return new Promise((resolve, reject) => {
    fetch(`${finalHost}${apiUrl}`, {
      method: 'POST',
      signal,
      headers,
      body: JSON.stringify(payload),
    })
      .then(async (response: any) => {
        if (!response.ok) {
          response
            .json()
            .then((data: any) => {
              if (!data.success) {
                const resData = {
                  message: data?.error,
                  status: response.status,
                  success: data.success,
                };
                resolve(resData);
              }
            })
            .catch((error: any) => reject(error));
        } else {
          const reader = response.body?.getReader();
          resolve({ reader }); // Return the expected `url`
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
}
export function sendTestMessageWithStream(
  apiUrl: string,
  payload: any,
  signal: any,
  host?: string,
): Promise<any> {
  // always send new JWT token
  return Auth.obtainJWTToken()
    .then((jwt) => {
      if (jwt) {
        Util.setCookie('JWT', jwt);
        return sendTestMessageWithStreamCall(apiUrl, payload, signal, host);
      }
      return Promise.resolve({});
    })
    .catch((error) => error);
}
export function getTemplates() {
  const apiUrl = '/agent-garden/templates';
  return getApi(apiUrl);
}

export function getParsedResponse<T>(formData: FormData): Promise<ApiResponse<T>> {
  const apiUrl = '/agent-garden/tools/openapi/parse';
  return postParsingApi(apiUrl, formData);
}
export function importPreviewForAgent<T>(formData: FormData): Promise<ApiResponse<T>> {
  const apiUrl = '/agent-garden/agents/import/preview';
  return postParsingApi(apiUrl, formData);
}
export function importAgent<T>(formData: FormData): Promise<ApiResponse<T>> {
  const apiUrl = '/agent-garden/agents/import';
  return postParsingApi(apiUrl, formData);
}

function postSchemaValidationApiCall<T = any>(apiUrl: string, payload: any):
Promise<ApiResponse<T>> {
  const host = `${Util.getAWSHostForAgentFoundation()}`;
  const jwt = Util.getCookie('JWT');
  // Note: Content-Type is automatically set by browser for FormData (multipart/form-data)
  // Do NOT set Content-Type manually as it will break the boundary parameter
  // Try minimal headers first to debug the "Unexpected HTTP header" error
  const headers: any = {
    Authorization: `Bearer ${jwt}`,
  };

  return new Promise((resolve, reject) => {
    fetch(`${host}${apiUrl}`, {
      method: 'POST',
      headers,
      body: payload,
    })
      .then((response) => {
        response.json().then((data: any) => {
          const resData = {
            data: data?.data,
            status: response.status,
            success: data.success,
            errors: data?.errors,
            error: data?.error,
            message: data?.message,
          };

          resolve(resData);
        }).catch((jsonError) => {
          reject(jsonError instanceof Error ? jsonError : new Error(String(jsonError)));
        });
      })
      .catch((error) => {
        reject(error instanceof Error ? error : new Error(String(error)));
      });
  });
}

export function postSchemaValidationApi<T = any>(apiUrl: string, payload: any):
Promise<ApiResponse<T>> {
  if (Auth.isJwtValid()) {
    return postSchemaValidationApiCall(apiUrl, payload);
  }

  return Auth.obtainJWTToken()
    .then((jwt) => {
      if (jwt) {
        Util.setCookie('JWT', jwt);
        return postSchemaValidationApiCall(apiUrl, payload);
      }
      return Promise.resolve({});
    })
    .catch((error) => error);
}

export function validateJsonSchema<T>(jsonContent: string, schemaType: 'input' | 'output'): Promise<ApiResponse<T>> {
  // Create a FormData object with the JSON content as a file
  // This handles both scenarios:
  // 1. JSON from file upload (ExFileUploader) - content is already in editor
  // 2. JSON from manual typing/copy-paste - content is typed/pasted into editor
  // In both cases, we create a virtual file from the current editor content

  // Validate JSON content before sending
  try {
    const parsedJson = JSON.parse(jsonContent);
    // eslint-disable-next-line
    console.log(parsedJson);
  } catch (jsonError) {
    throw new Error(`Invalid JSON: ${jsonError instanceof Error ? jsonError.message : 'Unknown JSON error'}`);
  }

  const formData = new FormData();
  const jsonBlob = new Blob([jsonContent], { type: 'application/json' });
  const fileName = `${schemaType}_schema.json`;

  // According to new API contract:
  formData.append('schema_data', jsonContent); // JSON schema as string
  formData.append('schema_file', jsonBlob, fileName); // JSON file
  formData.append('schema_type', 'json'); // Required: schema type

  // New endpoint for schema validation
  const apiUrl = '/agent-garden/agents/validate-schema';

  return postSchemaValidationApi(apiUrl, formData);
}

function postSchemaDownloadApiCall(apiUrl: string): Promise<any> {
  const host = `${Util.getAWSHostForAgentFoundation()}`;
  const jwt = Util.getCookie('JWT');
  const headers = {
    Authorization: `Bearer ${jwt}`,
  };

  return new Promise((resolve, reject) => {
    fetch(`${host}${apiUrl}`, {
      method: 'POST',
      headers,
    })
      .then((response) => {
        if (response.ok) {
          // For file downloads, return the response directly
          resolve(response);
        } else {
          response.json().then((data: any) => {
            reject(new Error(data?.error || `Download failed with status ${response.status}`));
          }).catch(() => {
            reject(new Error(`Download failed with status ${response.status}`));
          });
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export function postSchemaDownloadApi(apiUrl: string): Promise<any> {
  if (Auth.isJwtValid()) {
    return postSchemaDownloadApiCall(apiUrl);
  }

  return Auth.obtainJWTToken()
    .then((jwt) => {
      if (jwt) {
        Util.setCookie('JWT', jwt);
        return postSchemaDownloadApiCall(apiUrl);
      }
      return Promise.reject(new Error('Failed to obtain JWT token'));
    })
    .catch((error) => error);
}

export function downloadJsonSchema(agentId: string, schemaSource: 'input' | 'output'): Promise<any> {
  const apiUrl = `/agent-garden/agents/${agentId}/schemas/download?schema_source=${schemaSource}`;

  return postSchemaDownloadApi(apiUrl);
}

export function getBulkTools(toolIds: string[]): Promise<any> {
  const apiUrl = '/agent-garden/tools/bulk-retrieve';
  return postApi(apiUrl, toolIds);
}
export function getConversationStarters(objective: string) {
  const payload = {
    agent_objective: objective,
  };
  const apiUrl = '/agent-garden/ai-builder-assist/conversation-starters';
  return postApi(apiUrl, payload);
}

export function postFeedbackAgent(
  messageId: string,
  rating: string,
  comment: string,
  endpointUrl: string,
) {
  const payload = {
    rating,
    comment,
  };
  const apiUrl = `/api/v1/agent-garden-runtime/message/${messageId}/feedback`;
  return postApi(apiUrl, payload, undefined, endpointUrl);
}

export function getInstructions(objective: string, taskDescription: string) {
  const payload = {
    agent_objective: objective,
    task_description: taskDescription,
  };

  const apiUrl = '/agent-garden/ai-builder-assist/task-instructions';
  return postApi(apiUrl, payload);
}

export function getAgents(queryParams?: Record<string, any>) {
  const apiUrl = '/agent-garden/agents';
  const queryString = queryParams
    ? `?${
      Object.entries(queryParams)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&')}`
    : '';
  return getApi(`${apiUrl}${queryString}`);
}
export function getFavoriteAgents() {
  const apiUrl = '/agent-garden/agents?favorite=true&has_active_deployments=true';
  return getApi(apiUrl);
}
export function getConversations(endpointUrl: string, queryParams?: Record<string, any>) {
  const apiUrl = '/api/v1/agent-garden-runtime/sessions';
  const queryString = queryParams
    ? `?${
      Object.entries(queryParams)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&')}`
    : '';
  return getApi(`${apiUrl}${queryString}`, endpointUrl);
}
export function getAgentConversations(id: string, queryParams?: Record<string, any>) {
  const apiUrl = `/agent-garden-runtime/agent/${id}/sessions`;
  const queryString = queryParams
    ? `?${
      Object.entries(queryParams)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&')}`
    : '';
  return getApi(`${apiUrl}${queryString}`);
}
export function getAllMessagesFromSession(
  endpointUrl: string,
  id: string,
  queryParams?: Record<string, any>,
) {
  const apiUrl = `/api/v1/agent-garden-runtime/sessions/${id}/messages`;
  const queryString = queryParams
    ? `?${
      Object.entries(queryParams)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&')}`
    : '';
  return getApi(`${apiUrl}${queryString}`, endpointUrl);
}
export function deleteSession(id: string, endpointUrl?: string) {
  const apiUrl = `/api/v1/agent-garden-runtime/sessions/${id}`;
  return deleteApi(apiUrl, endpointUrl);
}
export function getBulkAgents(agentIds: any[]) {
  const apiUrl = '/agent-garden/agents/bulk-retrieve';
  return postApi(apiUrl, agentIds);
}
export function getAgent(id: string) {
  const apiUrl = `/agent-garden/agents/${id}`;
  return getApi(apiUrl);
}

export function getSamplePhrases(name: string, definition: string) {
  const payload = {
    guardrail_name: name,
    guardrail_definition: definition,
  };
  const apiUrl = '/agent-garden/ai-builder-assist/guardrail-sample-phrases';
  return postApi(apiUrl, payload);
}
export function toggleFavorite(agentId: string, isFavorite: boolean) {
  const payload = {
    agent_id: agentId,
    is_favorite: isFavorite,
  };
  const apiUrl = '/agent-garden/agents/favorites';
  return postApi(apiUrl, payload);
}
export function getAgentNameSuggestion(objective: string) {
  const payload = {
    agent_objective: objective,
  };
  const apiUrl = '/agent-garden/ai-builder-assist/agent-name';
  return postApi(apiUrl, payload);
}

export function generateAIAssistedAgentTemplate(objective: string, signal?: AbortSignal) {
  const payload = {
    agent_objective: objective,
  };
  const apiUrl = '/agent-garden/ai-builder-assist';
  return postApi(apiUrl, payload, signal);
}

export function createAgent(payload :any) {
  const apiUrl = '/agent-garden/agents';
  return postApi(apiUrl, payload);
}
export function createTool(payload :any, type: string) {
  const apiUrl = `/agent-garden/tools/${type}`;
  return postApi(apiUrl, payload);
}
export function installAgent(agentId :string) {
  const apiUrl = `/agent-garden/agents/${agentId}/install`;
  return postApi(apiUrl, {});
}
export function duplicateAgent(agentId :string) {
  const apiUrl = `/agent-garden/agents/${agentId}/duplicate`;
  return postApi(apiUrl, {});
}

export function exportAgentPreview(agentId :string) {
  const apiUrl = `/agent-garden/agents/${agentId}/export/preview`;
  return exportAgent(apiUrl, {});
}

export function exportAgentDownload(agentId :string) {
  const apiUrl = `/agent-garden/agents/${agentId}/export`;
  return agentExportDownload(apiUrl);
}

export function installTool(toolId :string, type: string) {
  const apiUrl = `/agent-garden/tools/${type}/${toolId}/install`;
  return postApi(apiUrl, {});
}
export function testTool(toolId: string, type: string, inputParameters: Record<string, unknown>) {
  const apiUrl = `/agent-garden/tools/${type}/${toolId}/test`;
  return postApi(apiUrl, { parameters: inputParameters });
}
export function uninstallTool(toolId :string, type: string) {
  const apiUrl = `/agent-garden/tools/${type}/${toolId}/uninstall`;
  return postApi(apiUrl, {});
}
export function duplicateTool(toolId :string, type: string) {
  const apiUrl = `/agent-garden/tools/${type}/${toolId}/duplicate`;
  return postApi(apiUrl, {});
}
export function uninstallAgent(agentId :string) {
  const apiUrl = `/agent-garden/agents/${agentId}/uninstall`;
  return postApi(apiUrl, {});
}
export function updateAgent(payload :any, agentId: string) {
  const apiUrl = `/agent-garden/agents/${agentId}`;
  return putApi(apiUrl, payload);
}
export function updateTool(payload :any, toolId: string, type: string) {
  const apiUrl = `/agent-garden/tools/${type}/${toolId}`;
  return putApi(apiUrl, payload);
}

export function getEnvironments(type: string) {
  const apiUrl = `/agent-garden/tools/${type}/environment-atom-attachments/`;
  return getApiWithAccountIdHeader(apiUrl);
}

export function getRepositories(type: string) {
  const apiUrl = `/agent-garden/tools/${type}/repositories/`;
  return getApi(apiUrl);
}

export function getUniverseData(type: string, respositoryId: string, universeId: string) {
  const apiUrl = `/agent-garden/tools/${type}/universe/?repository_id=${respositoryId}&universe_id=${universeId}`;
  return getApi(apiUrl);
}
export function getRecommendedAgentTemplatesList(queryParams?: Record<string, any>) {
  const apiUrl = '/agent-garden/templates/recommendations';
  const queryString = queryParams
    ? `?${
      Object.entries(queryParams)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&')}`
    : '';
  return getApi(`${apiUrl}${queryString}`);
}
export function getAgentTemplatesList(queryParams?: Record<string, any>) {
  const apiUrl = '/agent-garden/templates/list';
  const queryString = queryParams
    ? `?${
      Object.entries(queryParams)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&')}`
    : '';
  return getApi(`${apiUrl}${queryString}`);
}
export function importTemplate(templateId: string) {
  const apiUrl = '/agent-garden/templates/import-template';
  return postApi(apiUrl, { template_id: templateId });
}
export function getProcesses(
  type: string,
  environment_id: string,
  atom_id: string,
  query_token: string,
) {
  const apiUrl = `/agent-garden/tools/${type}/deployed-processes/?environment_id=${environment_id}&atom_id=${atom_id}`;
  return query_token
    ? getApiWithAccountIdHeader(apiUrl, query_token) : getApiWithAccountIdHeader(apiUrl);
}
export function getIntegrationProcesses(agent_id: any) {
  const apiUrl = `/agent-garden/agents/${agent_id}/process-id`;
  return getApi(`${apiUrl}`);
}
export function deleteAgent(agentId: string) {
  const apiUrl = `/agent-garden/agents/${agentId}`;
  return deleteApi(apiUrl);
}
export function deleteTool(id: string, type: string) {
  const apiUrl = `/agent-garden/tools/${type}/${id}`;
  return deleteApi(apiUrl);
}
export function getToolDetails(type: string, id: string) {
  const baseApiUrl = '/agent-garden/tools';
  const apiUrl = type && type !== 'all' ? `${baseApiUrl}/${type}` : baseApiUrl;

  return getApi(`${apiUrl}/${id}`);
}
export function getTools(queryParams?: Record<string, any>, type? :string) {
  const baseApiUrl = '/agent-garden/tools';
  const apiUrl = type && type !== 'all' ? `${baseApiUrl}/${type}` : baseApiUrl;

  let queryString = '';
  if (queryParams) {
    const paramsArr = Object.entries(queryParams).flatMap(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    });
    queryString = paramsArr.length ? `?${paramsArr.join('&')}` : '';
  }
  return getApi(`${apiUrl}${queryString}`);
}

export function getFilters() {
  const apiUrl = '/agent-garden/tools/filter-options';
  return getApi(apiUrl);
}

function convertCustomVariables(data: any): Record<string, string> {
  const customVariables = data?.data?.customVariables || [];
  const result: Record<string, string> = customVariables.reduce(
    (acc: Record<string, string>, variable: any) => {
      acc[variable.name] = variable.value;
      return acc;
    },
    {},
  );
  return result;
}

const transformDynamicProcessPropertiesToArray = (
  data: Record<string, { static_value: string | null; input_parameter_name: string | null }>,
): Array<{
  inputValue: { type: string; value: string };
  selectValue: { type: string; value : { data: string } };
}> => Object.entries(data).map(([key, value], index) => ({
  id: String(index),
  inputValue: {
    type: 'static',
    value: key, // Use the object key as the value for inputValue
  },
  selectValue: {
    type: value.input_parameter_name ? 'inputParam' : 'static',
    value: {
      data: value.input_parameter_name || value.static_value || '', // Use input_parameter_name or static_value
    },
  },
}));
const mapPersonalityTraits = (traits: any) => ({
  creativity: traits?.creativity?.toString() || '0',
  decisiveness: traits?.decisiveness?.toString() || '0',
  clarity: traits?.clarity?.toString() || '0',
  confidence: traits?.confidence?.toString() || '0',
  engagement: traits?.engagement?.toString() || '0',
  voice_tone: traits?.voice_tone?.toString() || 'Friendly',
});

const getInferenceConfiguration = (apiData: any) => {
  const performanceConfig = apiData.inference_configuration?.performance_config || {};
  const fileInferenceConfig = apiData.inference_configuration?.file_inference_config || {};

  return {
    performance_config: {
      processing_mode: performanceConfig?.processing_mode,
      rationale: performanceConfig?.rationale ?? true,
    },
    ...(fileInferenceConfig.allowed !== undefined && {
      file_inference_config: {
        allowed: fileInferenceConfig.allowed ?? false,
        types: fileInferenceConfig.types || [],
      },
    }),
  };
};
function transformApiResponseGlossariesToWizardFormat(apiGlossaries: any[]) {
  if (!apiGlossaries || !Array.isArray(apiGlossaries)) {
    return [];
  }

  return apiGlossaries
    .map((glossary: any) => {
      if (!glossary?.id || !glossary?.name) {
        return null;
      }
      return {
        id: glossary.id,
        name: glossary.name,
        purpose: glossary.purpose,
        accountId: glossary.account_id,
        contentId: glossary.content_id,
        contentServiceUrl: glossary.content_service_url,
      };
    })
    .filter((g: any) => g !== null);
}
function reverseTransformKnowledgeBase(knowledgeBase: any) {
  if (!knowledgeBase) return null;

  return {
    id: knowledgeBase.id,
    name: knowledgeBase.name,
    description: knowledgeBase.description,
    repository_id: knowledgeBase.repository_id,
    repository_name: knowledgeBase.repository_name,
    retrieval_mode: knowledgeBase.retrieval_mode,
    default_top_k: knowledgeBase.top_k,
    status: knowledgeBase.status,
    updated_at: knowledgeBase.updated_at,
    ...(Array.isArray(knowledgeBase.filters) && knowledgeBase.filters.length > 0
      ? { filters: knowledgeBase.filters }
      : {}),
  };
}
function reverseTransformKnowledgeBases(knowledgeBases: any[]) {
  if (!Array.isArray(knowledgeBases) || knowledgeBases.length === 0) {
    return [];
  }
  return knowledgeBases.map(reverseTransformKnowledgeBase).filter(Boolean);
}
export function transformApiResponseToWizardData(apiData: any, boomiModels: any[] = []) {
  const inferenceConfiguration = apiData.inference_configuration;
  const processingMode = inferenceConfiguration?.performance_config?.processing_mode?.toLowerCase();
  let model = null;

  if (['standard', 'quick'].includes(processingMode)) {
    const matchingModel = boomiModels.find(
      (m) => m.processing_mode?.toLowerCase() === processingMode,
    );
    model = matchingModel || null;
  } else if (isJevModel(inferenceConfiguration?.model)) {
    // A saved reference has no display name or processing mode; rehydrate it so the model
    // renders and so nothing mistakes its config id for a model configuration.
    model = buildJevModelFromReference(inferenceConfiguration.model);
  } else {
    model = inferenceConfiguration?.model;
  }

  return {
    profile: {
      completed: false,
      data: {
        personality_traits: mapPersonalityTraits(apiData.personality_traits),
        objective: apiData.objective || '',
        name: apiData.name || '',
        conversation_starters: apiData.conversation_starters || [],
        id: apiData.id || '',
        profile_picture: {
          avatar: apiData.profile_picture?.role,
          color: apiData.profile_picture?.colour,
        },
        inference_configuration: getInferenceConfiguration(apiData),
        // model: apiData.configuration.data || 'gpt-4',
        // Structured mode fields for existing agents
        agent_mode: apiData.agent_mode === 'structured' && isJevModel(inferenceConfiguration?.model)
          ? AGENT_MODE_DECISION
          : apiData.agent_mode || 'conversational',
        input_json_schema: apiData.input_schema || '{}',
        output_json_schema: apiData.output_schema || '{}',
        input_schema_id: apiData.input_schema_id || null,
        output_schema_id: apiData.output_schema_id || null,
        input_schema_type: apiData.input_schema_type || 'json',
        output_schema_type: apiData.output_schema_type || 'json',
      },
    },
    configuration: {
      data: {
        model,
      },
    },
    tasksAndTools: {
      completed: false,
      data: {
        tasks: (apiData.tasks || []).map((task: any) => ({
          id: Math.random().toString(36).substring(2, 9),
          description: task.objective || '',
          instructions: task.instructions || [],
          tools: task.tools || [],
          name: task.name || '',
          metahubGlossaries: transformApiResponseGlossariesToWizardFormat(task.meta_hub_glossaries),
          knowledgeBases: reverseTransformKnowledgeBases(task.knowledge_bases),
        })),
        tools: apiData.tools || [],
      },
    },
    guardrails: {
      completed: false,
      data: {
        blockedMessage: apiData.guardrails?.blocked_message || '',
        customRegex: (apiData.guardrails?.policies || [])
          .filter((policy: any) => policy.type === 'regex_pattern')
          .map((policy: any, index: number) => ({
            id: Date.now() + index,
            value: policy.configuration?.pattern || '',
          })),
        customWords: (apiData.guardrails?.policies || [])
          .filter((policy: any) => policy.type === 'word_filter')
          .map((policy: any) => (
            policy.configuration?.words || []
          ))
          .flat(),
        deniedTopicsValues: (apiData.guardrails?.policies || [])
          .filter((policy: any) => policy.type === 'denied_topic')
          .map(
            (policy: any, index: number) => ({
              id: Date.now() + index,
              name: policy.name || '',
              description: policy.configuration?.description || '',
              samplePhrases:
                (policy.configuration?.sample_phrases || []).map(
                  (phrase: string, idx: number) => ({
                    id: Date.now() + idx,
                    value: phrase,
                  }),
                ) || [],
            }),
          ),
      },
    },
    others: {
      completed: false,
      data: {
        customVariables: Object.entries(apiData.custom_variables || {}).map(
          ([key, value], index) => ({
            id: index,
            editable: false,
            name: key,
            value,
          }),
        ),
      },
    },
    packagesAndDeployments: {
      data: {
        packages_count: apiData.packages_count ?? 0,
        hasActiveDeployments: apiData.deployments
          ?.some((deployment: any) => deployment.deployment_status === 'ACTIVE') ?? false,
      },
    },
  };
}
const getPersonalityTraits = (traits: any) => ({
  voice_tone: traits.voice_tone || 'Friendly',
  creativity: parseInt(traits.creativity, 10),
  decisiveness: parseInt(traits.decisiveness, 10),
  clarity: parseInt(traits.clarity, 10),
  confidence: parseInt(traits.confidence, 10),
  engagement: parseInt(traits.engagement, 10),
});
const getProfilePicture = (profilePicture: any) => ({
  role: profilePicture?.avatar,
  colour: profilePicture?.color,
  image_id: 'img_location', // Assuming static image ID
});
function getGuardrailPolicies(wizardData: any) {
  const deniedTopics = (wizardData.guardrails.data.deniedTopicsValues || []).map((topic: any) => ({
    name: topic.name || '',
    type: 'denied_topic',
    configuration: {
      description: topic.description || '',
      sample_phrases: (topic.samplePhrases || []).map((phrase: any) => phrase.value),
    },
  }));

  const customRegex = (wizardData.guardrails.data.customRegex || []).map(
    (regex: any, index: any) => ({
      name: `Regex Pattern Validation_${index}`,
      type: 'regex_pattern',
      configuration: {
        pattern: regex.value || '',
      },
    }),
  );

  const customWords = wizardData.guardrails.data.customWords?.length
    ? [
      {
        name: 'Prohibited Terms Policy',
        type: 'word_filter',
        configuration: {
          words: wizardData.guardrails.data.customWords || [],
        },
      },
    ]
    : [];

  return [...deniedTopics, ...customRegex, ...customWords];
}
function getGuardrails(wizardData: any) {
  const policies = getGuardrailPolicies(wizardData);
  return policies.length > 0
    ? {
      blocked_message: wizardData.guardrails.data.blockedMessage || '',
      policies,
    }
    : null;
}
function formatInstructionsIntoSingleStringArray(instructions: any) {
  if (!instructions || !Array.isArray(instructions) || instructions.length === 0) {
    return [];
  }
  return [instructions.join('\n')];
}
function transformMetahubGlossaries(glossaries: any[]) {
  if (!glossaries || !Array.isArray(glossaries)) {
    return [];
  }

  return glossaries.map((glossary: any) => ({
    id: glossary.id,
    name: glossary.name,
    purpose: glossary.purpose,
    content_id: glossary.contentId,
    account_id: glossary.accountId,
    content_service_url: glossary.contentServiceUrl
      ? glossary.contentServiceUrl
      : undefined,
  })).filter((g: any) => g.id && g.name);
}

function transformKnowledgeBase(knowledgeBase: any) {
  if (!knowledgeBase) return null;

  return {
    id: knowledgeBase.id,
    name: knowledgeBase.name,
    description: knowledgeBase.description,
    repository_id: knowledgeBase.repository_id,
    repository_name: knowledgeBase.repository_name,
    retrieval_mode: knowledgeBase.retrieval_mode,
    top_k: knowledgeBase.default_top_k,
    status: knowledgeBase.status,
    ...(Array.isArray(knowledgeBase.filters) && knowledgeBase.filters.length > 0
      ? { filters: knowledgeBase.filters }
      : {}),
  };
}

function transformKnowledgeBases(knowledgeBases: any[]) {
  if (!Array.isArray(knowledgeBases) || knowledgeBases.length === 0) {
    return [];
  }
  return knowledgeBases.map(transformKnowledgeBase).filter(Boolean);
}
export function transformWizardDataToApiPayload(wizardData: any) {
  const customVariables = convertCustomVariables(wizardData.others);
  const guardrails = getGuardrails(wizardData);

  // Check agent mode first to determine which fields to include
  const agentMode = wizardData?.profile?.data?.agent_mode;
  const isStructured = isSchemaMode(agentMode);
  const model = null;
  const inferenceConfiguration: any = {
    performance_config: {
      processing_mode: wizardData?.profile?.data?.inference_configuration?.performance_config?.processing_mode || 'standard',
      rationale: wizardData?.profile?.data?.inference_configuration
        ?.performance_config?.rationale ?? true,
    },
    model,
  };
  if (wizardData?.configuration?.data?.model && wizardData?.configuration?.data?.model?.processing_mode === 'quick') {
    inferenceConfiguration.performance_config.processing_mode = 'quick';
  }
  if (wizardData?.configuration?.data?.model
    && (wizardData?.configuration?.data?.model?.configuration_id
      || wizardData?.configuration?.data?.model?.config_id)) {
    inferenceConfiguration.model = {
      config_id: wizardData?.configuration?.data?.model?.configuration_id
      || wizardData?.configuration?.data?.model?.config_id,
      provider: wizardData?.configuration?.data?.model?.provider,
      model_id: wizardData?.configuration?.data?.model?.model_id,
    };
    inferenceConfiguration.performance_config.processing_mode = null;
  }
  // Include file_inference_config inside inference_configuration
  // if file uploads are enabled
  const fileInferenceConfig = wizardData?.profile?.data
    ?.inference_configuration?.file_inference_config;
  const fileUploadsAllowed = fileInferenceConfig?.allowed === true;
  inferenceConfiguration.file_inference_config = {
    allowed: fileUploadsAllowed,
    types: fileInferenceConfig?.types || [],
  };
  // If model_id is jev/system-one, set file_inference_config and performance_config to null
  if (inferenceConfiguration.model?.model_id === 'jev/system-one') {
    inferenceConfiguration.file_inference_config = null;
    inferenceConfiguration.performance_config = null;
  }
  const basePayload: any = {
    objective: wizardData.profile.data.objective || '',
    name: wizardData.profile.data.name || '',
    status: 'DRAFT',
    provider_name: 'Boomi',
    provider_id: 'boomi',
    installed_on: null,
    // model: wizardData.configuration.data || 'gpt-4',
    profile_picture: getProfilePicture(wizardData.profile.data.profile_picture),
    tasks: (wizardData.tasksAndTools.data.tasks || []).map((task: any) => ({
      name: task.name || 'default_task_name',
      objective: task.description || '',
      instructions: formatInstructionsIntoSingleStringArray(task.instructions),
      tools: task.tools || [],
      meta_hub_glossaries: transformMetahubGlossaries(task.metahubGlossaries),
      knowledge_bases: transformKnowledgeBases(task.knowledgeBases),
    })),
    // tools: (wizardData.tasksAndTools.data.tools || []),
    guardrails,
    ...(Object.keys(customVariables).length > 0 && { custom_variables: customVariables }),
    inference_configuration: inferenceConfiguration,
  };

  // Only include conversational fields for non-structured agents
  if (!isStructured) {
    basePayload.personality_traits = getPersonalityTraits(wizardData.profile
      .data.personality_traits);
    basePayload.conversation_starters = wizardData.profile.data.conversation_starters || [];
  }

  // Conditionally include structured mode fields (using existing isStructured variable)
  if (isStructured) {
    basePayload.agent_mode = toBackendAgentMode(agentMode);
    basePayload.input_schema_type = wizardData?.profile?.data?.input_schema_type || 'json';
    basePayload.input_schema = wizardData?.profile?.data?.input_json_schema ?? '';
    basePayload.output_schema_type = wizardData?.profile?.data?.output_schema_type || 'json';
    basePayload.output_schema = wizardData?.profile?.data?.output_json_schema ?? '';

    // Preserve schema IDs for existing agents (important for updates)
    if (wizardData?.profile?.data?.input_schema_id) {
      basePayload.input_schema_id = wizardData.profile.data.input_schema_id;
    }
    if (wizardData?.profile?.data?.output_schema_id) {
      basePayload.output_schema_id = wizardData.profile.data.output_schema_id;
    }
  } else {
    // Ensure conversation mode is explicitly set
    basePayload.agent_mode = 'conversational';
  }
  return basePayload;
}
function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
const updateKeyNames = (
  inputParameters: any[],
): { inputName: string; inputDescription: string; required: boolean;
  inputType: string }[] => inputParameters?.map((param) => ({
  inputName: param.name, // update 'name' to 'inputValue'
  inputDescription: param.description, // update 'description' to 'descriptionValue'
  required: param.required, // keep 'required' as is
  inputType: capitalize(param.type || ''), // keep 'type' as is
}));
const updateKeyNamesForMCP = (
  inputParameters: any[],
): { inputName: string; inputDescription: string; required: boolean;
  inputType: string; defaultValue: string;
  serverDefaultValue: string }[] => inputParameters?.map((param) => ({
  inputName: param.name, // update 'name' to 'inputValue'
  inputDescription: param.description, // update 'description' to 'descriptionValue'
  required: param.required, // keep 'required' as is
  inputType: capitalize(param.type || ''), // keep 'type' as is
  defaultValue: param.user_default || param.server_default || '',
  serverDefaultValue: param.server_default || '',
}));
const updateExampleKeyNames = (
  examples: { input: string; output: string; }[],
): { exampleInput: string; expectedOutput: string; }[] => examples.map((param) => ({
  exampleInput: param.input, // update 'name' to 'inputValue'
  expectedOutput: param.output, // update 'description' to 'descriptionValue',
}));

const getDataType = (item: any, apiData: any) => {
  const result = apiData.fields.find((field: any) => field.unique_id === item.field_id);
  return result ? result.data_type : '';
};
export function generateUniqueKey(index: number) {
  return `${Date.now()}-${Math.floor(Math.random() * 1000)}-${index}`;
}
export function getDisplayValue(selectValue: any) {
  if (!selectValue?.value) return '';
  const displayValue = typeof selectValue?.value?.data === 'string'
    ? selectValue?.value?.data
    : selectValue?.value;
  return selectValue?.type === 'inputParam' ? `{{${displayValue}}}` : displayValue;
}
const transformFilterFields = (apiData: any) => {
  // Map through filter_fields to transform the data
  const mappedObject = apiData?.filter_fields.map((item: any) => {
    // Find the matching field
    const matchingField = apiData?.fields.find((field: any) => field.unique_id === item.field_id);

    return {
      fieldName: matchingField ? matchingField.pretty_name : undefined,
      field_id: item.field_id,
      operator: item.operator,
      values: item.values,
      time: '',
      date: '',
      repeatable: false,
      filter_operator: '',
      data_type: getDataType(item, apiData),
      operators: [],
    };
  });

  return mappedObject || [];
};
export function transformAPIResponseToIntegrationData(apiData: any) {
  return {
    id: apiData.id,
    toolBasicInfo: {
      toolName: apiData?.name,
      toolDescription: apiData?.description,
      environment_id: apiData?.environment_id,
      atom: apiData?.atom_id,
      process: apiData?.process_id,
      status: apiData?.tool_status,
    },
    inputParameters: updateKeyNames(apiData?.input_parameters),
    dynamicProcess: transformDynamicProcessPropertiesToArray(apiData?.dynamic_process_properties),
    unique_name: 'a_198',
    tool_status: 'ACTIVE',
  };
}
export function handleError(error: any, prefixMessage: string) {
  if (error?.message || error?.error) {
    ToastController.show({
      description: error.message || error.error,
      type: AlertToastType.ERROR,
    });
  } else {
    ToastController.show({
      description: `${prefixMessage}`,
      type: AlertToastType.ERROR,
    });
  }
}
export function transformAPIResponseToPromptData(apiData: any) {
  return {
    id: apiData.id,
    toolBasicInfo: {
      toolName: apiData?.name,
      toolDescription: apiData?.description,
      status: apiData?.tool_status,
    },
    inputParameters: updateKeyNames(apiData?.input_parameters),
    examples: updateExampleKeyNames(apiData?.examples),
    promptDescription: apiData?.prompt,
    unique_name: 'a_198',
    tool_status: 'ACTIVE',
  };
}
export function transformAPIResponseToMCPToolData(apiData: any) {
  return {
    id: apiData.id,
    toolBasicInfo: {
      toolName: apiData?.name,
      toolDescription: apiData?.description,
      status: apiData?.tool_status,
      server_id: apiData?.server_id,
    },
    inputParameters: updateKeyNamesForMCP(apiData?.input_parameters),
    sourceDetails: apiData?.source_details,
    source_name: apiData?.source_details?.name,
  };
}
export function transformAPIResponseToHubData(apiData: any) {
  return {
    id: apiData.id,
    toolBasicInfo: {
      toolName: apiData?.name,
      toolDescription: apiData?.description,
      status: apiData?.tool_status,
      repository: {},
      universe: {},
    },
    inputParameters: updateKeyNames(apiData?.input_parameters),
    repository: apiData?.repository_id,
    universe: apiData?.universe_id,
    unique_name: 'a_198',
    tool_status: 'ACTIVE',
    filterDatas: transformFilterFields(apiData),
    filter_operator: apiData?.filter_operator || '',
  };
}

export function mapSelectValue(inputParamName: string, staticValue: string, wrapValue = false) {
  if (inputParamName) return { type: 'inputParam', value: inputParamName };
  return {
    type: 'static',
    value: wrapValue ? { data: staticValue || '' } : (staticValue || ''),
  };
}

export function mapQueryParameters(queryParams: any[]) {
  return (queryParams || []).map((query: any, index) => ({
    0: { queryName: '', queryValue: '' },
    id: generateUniqueKey(index),
    name: query.name || '',
    selectValue: mapSelectValue(query.input_parameter_name, query.static_value),
  }));
}

export function mapPathParameters(pathParams: any[]) {
  return (pathParams || []).map((path: any, index) => ({
    0: { pathName: '', pathValue: '' },
    id: generateUniqueKey(index),
    name: path.name || '',
    selectValue: mapSelectValue(path.input_parameter_name, path.static_value, true),
  }));
}

export function mapHeaders(headers: any[]) {
  return (headers || []).map((header: any, index) => ({
    0: { headerName: '', headerValue: '' },
    id: generateUniqueKey(index),
    name: header.name || '',
    selectValue: mapSelectValue(header.input_parameter_name, header.static_value, true),
    isSensitive: header.is_sensitive || false,
  }));
}

export function getAuthentication(authentication: any) {
  if (!authentication) return null;
  const getConfig = () => {
    if (authentication.type === 'oauth' && authentication.config) {
      return {
        ...authentication.config,
        authorization_server_request_params: transformParamsFromApi(
          authentication.config.authorization_server_request_params || [],
        ),
        access_token_request_params: transformParamsFromApi(
          authentication.config.access_token_request_params || [],
        ),
      };
    }
    return authentication.config || {};
  };
  return { type: authentication.type || 'none', config: getConfig() };
}

export function transformAPIResponseToOpenAPIData(apiData: any) {
  return {
    id: apiData?.id || '',
    toolBasicInfo: {
      toolName: apiData?.name || '',
      toolDescription: apiData?.description || '',
      status: apiData?.tool_status || '',
    },
    inputParameters: (apiData?.input_parameters || []).map((param: any) => ({
      inputName: param.name || '',
      inputDescription: param.description || '',
      inputType: param.type ? param.type.charAt(0).toUpperCase() + param.type.slice(1) : '',
      required: param.required || false,
    })),
    examples: [],
    source_name: apiData?.source_name || null,
    source_id: apiData?.source_id || null,
    source_type: apiData?.source_type || null,
    configuration: [{
      method: apiData?.method || '',
      path: apiData?.path || '',
      description: apiData?.description || '',
      name: apiData?.name || '',
      base_url: apiData?.base_url || '',
      input_parameters: [],
      query_parameters: mapQueryParameters(apiData?.query_parameters),
      path_parameters: mapPathParameters(apiData?.path_parameters),
      headers: mapHeaders(apiData?.headers),
      request_body: {
        type: apiData?.request_body?.type || 'none',
        template: apiData?.request_body?.template,
      },
      authentication: getAuthentication(apiData?.authentication),
    }],
  };
}

export const generateInputParameters = async (requestData: {
  name: string;
  description: string;
  method?: string;
  base_url?: string;
  path?: string;
  query_parameters?: Array<{ name: string }>;
  headers?: Array<{ name: string }>;
  request_type?: string;
  request_body?: any;
  selected_param_names?: string[];
  exclude_param_names?: string[];
  tool_id?: string;
  tool_type?: string;
}) => {
  try {
    const response = await postApiFullCall(
      '/agent-garden/ai-builder-assist/generate-input-parameters',
      requestData,
    );

    // If backend returns a non-success envelope, show toast
    if (!(response as any)?.success) {
      const msg = (response as any)?.error || (response as any)?.message || 'Failed to generate input parameters. Please try again.';
      ToastController.show({
        description: msg,
        type: AlertToastType.ERROR,
      });
    }

    return response;
  } catch (error: any) {
    // Network/parse errors
    const msg = error?.message || 'Network error while generating input parameters. Please check your connection and try again.';
    ToastController.show({
      description: msg,
      type: AlertToastType.ERROR,
    });
    // keep a consistent shape for callers
    return { success: false, status: 0, error: msg };
  }
};

export function getAgentsEquipped(toolId: string, queryParams?: Record<string, any>) {
  let queryString = '';
  if (queryParams) {
    queryString = `?${Object.entries(queryParams)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&')}`;
  }
  const apiUrl = `/agent-garden/tools/${toolId}/agents-equipped${queryString}`;
  return getApi(apiUrl);
}

export function getApplicationAuthorizationStatus(
  agentId: string,
  applicationId: string,
  toolId: string,
  applicationName: string,
  endpointUrl: string,
  deploymentId: string,
) {
  let apiUrl = '';
  if (deploymentId) {
    apiUrl = `/api/v1/agent-garden-runtime/deployment/${deploymentId}/application-authorization-status`;
  } else {
    apiUrl = `/api/v1/agent-garden-runtime/agent/${agentId}/application-authorization-status`;
  }
  const queryParams = {
    application_id: applicationId,
    tool_id: toolId,
    application_name: applicationName,
  };
  const queryString = queryParams
    ? `?${
      Object.entries(queryParams)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&')}`
    : '';
  return getApi(`${apiUrl}${queryString}`, endpointUrl);
}

export default agentFoundationApiService;
